import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { getQuestionnaireResponse, submitQuestionResponse } from '@api/questionnaire';
import { QuestionnaireQuestion, QuestionnaireRecommendation, QuestionnaireResponse } from 'types/questionnaire';
import { EmailCaptureFields } from 'types/layout/lead';
import { getDistinctId } from '@utils/analytics';
import { serviceLevelWithPlansByName } from '@utils/service-levels';
import store from '..';

@Module({
	dynamic: true,
	namespaced: true,
	name: 'questionnaire',
	store
})
class QuestionnaireModule extends VuexModule {
	email: string | undefined;
	emailCaptureFields: EmailCaptureFields = { existingUser: false, mixpanelId: '', newLead: false };
	fullResponse: QuestionnaireResponse = { questions: [] };
	dataRetrieved = false;

	get distinctId(): string | undefined {
		if (this.emailCaptureFields.mixpanelId) {
			return this.emailCaptureFields.mixpanelId;
		}

		return getDistinctId();
	}

	get recommendation(): QuestionnaireRecommendation {
		const result: QuestionnaireRecommendation = {};
		if (this.fullResponse.recommendedServiceLevel) {
			result.serviceLevel = this.fullResponse.recommendedServiceLevel;
		}

		if (
			this.fullResponse.recommendedRiaPlan &&
			serviceLevelWithPlansByName(this.fullResponse.recommendedServiceLevel)
		) {
			result.riaPlan = this.fullResponse.recommendedRiaPlan;
		}
		return result;
	}

	@Action
	public async getFullResponse(ignore404?: boolean): Promise<void> {
		if (this.distinctId) {
			const fullResponse = await getQuestionnaireResponse(!!ignore404);
			this.UPDATE_DATA_RETRIEVED(true);
			this.UPDATE_FULL_RESPONSE(fullResponse);
		}
	}

	@Action
	public async submitQuestionResponse(question: QuestionnaireQuestion): Promise<void> {
		if (this.distinctId) {
			const resultQuestion = await submitQuestionResponse(question.id, question.questionResponse);
			this.UPDATE_QUESTION_RESPONSE(resultQuestion);
		}
	}

	@Mutation
	public UPDATE_FULL_RESPONSE(state: QuestionnaireResponse): void {
		if (this.fullResponse.questions.length > 0) {
			state.questions.forEach((question) => {
				const oldQuestion = this.fullResponse.questions.find((old) => old.id == question.id);
				if (oldQuestion) {
					question.questionResponse = oldQuestion.questionResponse;
				}
			});
		}
		this.fullResponse = state;
	}

	@Mutation
	public UPDATE_EMAIL(email: string): void {
		this.email = email;
	}

	@Mutation
	public UPDATE_EMAIL_CAPTURE_FIELDS(newFields: EmailCaptureFields): void {
		this.emailCaptureFields = newFields;
	}

	@Mutation
	public UPDATE_QUESTION_RESPONSE(question: QuestionnaireQuestion): void {
		const questionToUpdate = this.fullResponse.questions.find((q) => q.id === question.id);
		if (questionToUpdate) {
			questionToUpdate.questionResponse = question.questionResponse;
			questionToUpdate.nextQuestion = question.nextQuestion;
		}
	}

	@Mutation
	public UPDATE_DATA_RETRIEVED(newValue: boolean): void {
		this.dataRetrieved = newValue;
	}
}

export const questionnaire = getModule(QuestionnaireModule);
